import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Messages } from '../../../codegen/Localize';
import { SiteSpaceContentTypeChangeEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { Telemetry } from '../../../commonInfra/services/Telemetry';
import { getExplorableItemDefinition } from '../../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { NullProjectIdForNonProjects } from '../../state/SpaceInfo';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { CreateContentButtonLocations } from '../Explorer/CreateContent/CreateContentButton';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { addQuery, SpacesSiteUrls } from '../SpacesSiteUrls';
import { createContentButton } from '../SubspaceHelpers';
import { SiteLevelFilterByTypeDropdown } from './SiteLevelFilterByTypeDropdown';

export interface SiteSpaceProps {
  readonly siteSpaceContentType: SiteSpaceContentType;
  readonly urls: SpacesSiteUrls;
}

export class SiteSpace extends React.PureComponent<SiteSpaceProps> {

  public render() {
    const displayModeContentType = convertSiteContentTypeToContentType(this.props.siteSpaceContentType);
    const supportedDisplayModes = getExplorableItemDefinition(displayModeContentType).supportedDisplayModes;
    return (
      <div className={ SpaceContainerStyles.SpaceContainer }>
        <div className={ SpaceContainerStyles.MastheadArea }>
          <div className={ExplorerStyles.SpaceHeaderContent}>
            <div className={ExplorerStyles.SpaceHeaderText}>{Messages.explore()}</div>
            {this.contentFilterControl()}
          </div>
        </div>
        <div className={ SpaceContainerStyles.SubplaceArea }>
          <ConnectedExplorer
            supportedDisplayModes={supportedDisplayModes}
            toolbarProps= {{
              supportFilterPanel: true,
              toolbarVisibility: 'enabled',
              toolbarContentSpecificControls: { beginning: createContentButton({
                location: CreateContentButtonLocations.SiteSpace,
                projectId: NullProjectIdForNonProjects
              }) }
            }}
          />
        </div>
      </div>
    );
  }

  private changeSiteLevelContentType = (contentType: SiteSpaceContentType) => {
    this.sendSiteSpaceTelemetryEvent(contentType);

    const url = addQuery(getSiteUrlForContentType(contentType, this.props.urls));
    window.location.href = url;
  }

  private sendSiteSpaceTelemetryEvent(newContentType: SiteSpaceContentType) {
    const oldContentType = this.props.siteSpaceContentType;
    Telemetry.sendEvent<SiteSpaceContentTypeChangeEvent>({
      type: TelemetryEvents.SiteSpaceContentTypeChange,
      newContentType,
      oldContentType,
    });
  }

  private contentFilterControl(): JSX.Element {
    return (
        <SiteLevelFilterByTypeDropdown currentContentType={this.props.siteSpaceContentType}
                                        changeCurrentContentType={this.changeSiteLevelContentType} />
    );
  }
}

// Return the top level url for the given content type.
function getSiteUrlForContentType(contentType: SiteSpaceContentType, urls: SiteUrls) {
  if (contentType === 'dataconnection') {
    throw new Error('invalid site-level content type: dataconnection');
  }

  const ContentTypeToUrl: Record<SiteSpaceContentType, string> = {
    dataconnection: '',
    databaseWithCrass: '',  // handled separately
    tableWithCrass: '',  // handled separately
    database: '',  // handled separately
    table: '',  // handled separately
    collection: '',
    datasource: urls.datasourcesUrl(),
    dataRole: urls.dataRolesUrl(),
    lens: urls.lensesUrl(),
    explore: urls.exploreUrl(),
    flow: urls.flowsUrl(),
    project: urls.projectsUrl(),
    view: urls.viewsUrl(),
    workbook: urls.workbooksUrl(),
    metric: urls.metricsUrl(),
    publishedConnection: urls.publishedConnectionsUrl()
  };

  const url = ContentTypeToUrl[contentType];
  if (url) {
    return url;
  }

  throw new Error('invalid site-level content type: ' + contentType);
}
